import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import {
  FileText, TrendingUp, Briefcase, Award, ChevronRight,
  Loader2, Zap, ArrowRight, Bell, CheckCircle, Clock, Star, MapPin, Gift, Lightbulb,
  DollarSign, Target, Users
} from 'lucide-react';
import { partnerCasesAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { SchemeCountdown } from '@/components/SchemeCountdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTIVITY_ICONS = {
  lead_created: { icon: FileText, color: 'text-blue-500 bg-blue-50' },
  report_ready: { icon: Zap, color: 'text-green-500 bg-green-50' },
  mca_review: { icon: Clock, color: 'text-amber-500 bg-amber-50' },
  lead_converted: { icon: Star, color: 'text-violet-500 bg-violet-50' },
  case_assigned: { icon: Briefcase, color: 'text-[#1e3a5f] bg-[#1e3a5f]/10' },
};

const TIER_COLORS = {
  'R-PACE Partner': 'bg-slate-100 text-slate-600 border-slate-200',
  'R-PACE Pro': 'bg-amber-50 text-amber-700 border-amber-200',
  'R-PACE Elite': 'bg-violet-50 text-violet-700 border-violet-200',
};

function fmtINR(n) {
  if (!n && n !== 0) return '\u20B90';
  return '\u20B9' + Math.round(n).toLocaleString('en-IN');
}

export default function PartnerDashboard() {
  const { partner } = useOutletContext();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availableLeads, setAvailableLeads] = useState([]);
  const [poolCases, setPoolCases] = useState([]);
  const [claimingId, setClaimingId] = useState(null);
  const [claimingCaseId, setClaimingCaseId] = useState(null);
  const [tipOfDay, setTipOfDay] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('rpace_token');
    Promise.all([
      fetch(`${API_URL}/api/partners/dashboard-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => r.json()),
      partnerCasesAPI.getAvailableLeads().then(r => r.data).catch(() => []),
      fetch(`${API_URL}/api/partners/tip-of-the-day`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => r.json()).catch(() => null),
      fetch(`${API_URL}/api/partners/cases/pool`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => r.ok ? r.json() : []).catch(() => []),
    ])
      .then(([statsData, leadsData, tipData, poolData]) => {
        setStats(statsData);
        setAvailableLeads(leadsData);
        setPoolCases(poolData || []);
        if (tipData && tipData.title) setTipOfDay(tipData);
      })
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  const handleClaim = async (leadId) => {
    setClaimingId(leadId);
    try {
      await partnerCasesAPI.claimLead(leadId);
      toast.success('Lead claimed! It\'s now in your pipeline.');
      setAvailableLeads(prev => prev.filter(l => l.id !== leadId));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to claim lead');
    } finally { setClaimingId(null); }
  };

  const handleClaimCase = async (caseId) => {
    setClaimingCaseId(caseId);
    try {
      const token = localStorage.getItem('rpace_token');
      const res = await fetch(`${API_URL}/api/partners/cases/${caseId}/claim`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Claim failed');
      }
      toast.success('Case claimed! Opening...');
      setPoolCases(prev => prev.filter(c => c.id !== caseId));
      window.location.href = `/partner/cases/${caseId}`;
    } catch (err) {
      toast.error(err?.message || 'Failed to claim case');
    } finally { setClaimingCaseId(null); }
  };

  if (loading) return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!stats) return null;

  const { summary, credits, activity } = stats;
  const firstName = (stats.partner_name || '').split(' ')[0];
  const tierColor = TIER_COLORS[credits.tier] || TIER_COLORS['R-PACE Partner'];
  const progressToNext = credits.next_tier_credits ? Math.min((credits.total / credits.next_tier_credits) * 100, 100) : 100;

  return (
    <div className="max-w-5xl mx-auto px-6 py-8" data-testid="partner-dashboard">
      {/* Greeting */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900" data-testid="partner-greeting">
          Hi {firstName}!
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {stats.firm_name && <span>{stats.firm_name} &bull; </span>}
          {stats.profession === 'CA' ? 'Chartered Accountant' : 'Company Secretary'}
        </p>
      </div>

      {/* CCFS Countdown Banner */}
      <div className="mb-4">
        <SchemeCountdown variant="partner" />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="summary-cards">
        {/* Leads: Submitted vs Received */}
        <Card className="border-l-4 border-l-blue-500 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/r-pace/pipeline')}>
          <CardContent className="p-4">
            <p className="text-[10px] text-slate-500 uppercase font-medium mb-2">Leads</p>
              <div className="flex items-end gap-3">
                <div>
                  <p className="text-2xl font-bold text-slate-900" data-testid="stat-leads-submitted">{summary.leads_submitted}</p>
                  <p className="text-[10px] text-slate-400">Submitted</p>
                </div>
                <div className="text-slate-300 text-lg">|</div>
                <div>
                  <p className="text-2xl font-bold text-blue-600" data-testid="stat-leads-received">{summary.leads_received}</p>
                  <p className="text-[10px] text-slate-400">Received</p>
                </div>
              </div>
              {summary.reports_ready > 0 && (
                <p className="text-[10px] text-green-600 mt-1.5 flex items-center gap-1"><Zap className="w-3 h-3" /> {summary.reports_ready} report{summary.reports_ready > 1 ? 's' : ''} ready</p>
              )}
            </CardContent>
          </Card>

          {/* Savings: Potential vs Actual */}
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="p-4">
              <p className="text-[10px] text-slate-500 uppercase font-medium mb-2">Client Savings Impact</p>
              <div className="flex items-end gap-3">
                <div>
                  <p className="text-lg font-bold text-slate-900" data-testid="stat-potential-savings">{fmtINR(summary.potential_savings)}</p>
                  <p className="text-[10px] text-slate-400">Potential</p>
                </div>
                <ArrowRight className="w-3 h-3 text-slate-300 mb-1.5" />
                <div>
                  <p className="text-lg font-bold text-emerald-600" data-testid="stat-actual-savings">{fmtINR(summary.actual_savings)}</p>
                  <p className="text-[10px] text-slate-400">Realized</p>
                </div>
              </div>
              {summary.potential_savings > 0 && (
                <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2">
                  <div className="bg-emerald-500 h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, summary.potential_savings > 0 ? (summary.actual_savings / summary.potential_savings) * 100 : 0)}%` }} />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Fee Potential vs Billed */}
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <p className="text-[10px] text-slate-500 uppercase font-medium mb-2">Fee Potential</p>
              <div className="flex items-end gap-3">
                <div>
                  <p className="text-lg font-bold text-slate-900" data-testid="stat-fee-potential">{fmtINR(summary.fee_potential)}</p>
                  <p className="text-[10px] text-slate-400">Potential</p>
                </div>
                <ArrowRight className="w-3 h-3 text-slate-300 mb-1.5" />
                <div>
                  <p className="text-lg font-bold text-amber-600" data-testid="stat-billed">{fmtINR(summary.billed)}</p>
                  <p className="text-[10px] text-slate-400">Billed</p>
                </div>
              </div>
              {summary.fee_potential > 0 && (
                <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2">
                  <div className="bg-amber-500 h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, summary.fee_potential > 0 ? (summary.billed / summary.fee_potential) * 100 : 0)}%` }} />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Active Engagements + Credits */}
          <Card className="border-l-4 border-l-violet-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="cursor-pointer" onClick={() => navigate('/r-pace/engagements')}>
                  <p className="text-[10px] text-slate-500 uppercase font-medium mb-1">Engagements</p>
                  <p className="text-2xl font-bold text-slate-900" data-testid="stat-cases-active">{summary.cases_active}</p>
                  <p className="text-[10px] text-slate-400">active</p>
                </div>
                <div className="border-l border-slate-200 pl-4 cursor-pointer" onClick={() => navigate('/r-pace/credits')}>
                  <p className="text-[10px] text-slate-500 uppercase font-medium mb-1">Credits</p>
                  <p className="text-2xl font-bold text-violet-600" data-testid="stat-credits">{credits.total}</p>
                  <p className="text-[10px] text-slate-400">{credits.tier}</p>
                </div>
              </div>
            </CardContent>
          </Card>
      </div>

      {/* Lead Pipeline Summary (clickable stages) */}
      <Card className="mb-8" data-testid="pipeline-summary">
        <CardContent className="p-5">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-3">Lead Pipeline</p>
          <div className="flex items-center gap-1.5">
            {[
              { key: 'new', label: 'New', color: 'bg-blue-500' },
              { key: 'mca_downloaded', label: 'MCA', color: 'bg-cyan-500' },
              { key: 'quick_report_sent', label: 'Report', color: 'bg-indigo-500' },
              { key: 'call_scheduled', label: 'Call Set', color: 'bg-amber-500' },
              { key: 'call_done', label: 'Called', color: 'bg-sky-500' },
              { key: 'detailed_report', label: 'Detail', color: 'bg-violet-500' },
              { key: 'won', label: 'Won', color: 'bg-emerald-500' },
              { key: 'lost', label: 'Lost', color: 'bg-slate-400' },
            ].map((stage) => {
              const count = (stats.pipeline_counts || {})[stage.key] || 0;
              return (
                <button
                  key={stage.key}
                  onClick={() => navigate(`/r-pace/pipeline?stage=${stage.key}`)}
                  className="flex-1 rounded-lg border border-slate-100 hover:border-slate-300 hover:shadow-sm transition-all p-2.5 text-center"
                  data-testid={`pipeline-stage-${stage.key}`}
                >
                  <div className={`w-2 h-2 rounded-full ${stage.color} mx-auto mb-1.5`} />
                  <p className="text-lg font-bold text-slate-800 tabular-nums">{count}</p>
                  <p className="text-[9px] text-slate-400 font-medium uppercase tracking-wide">{stage.label}</p>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Tip of the Day */}
      {tipOfDay && (
        <Card className="mb-8 border-l-4 border-l-amber-400 bg-amber-50/30" data-testid="tip-of-the-day">
          <CardContent className="p-4 flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0 mt-0.5">
              <Lightbulb className="w-4 h-4 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-amber-600 font-bold uppercase tracking-wide">Tip of the Day</p>
              <p className="text-sm font-medium text-slate-800 mt-0.5" data-testid="tip-title">{tipOfDay.title}</p>
              <p className="text-xs text-slate-500 mt-1" data-testid="tip-content">{tipOfDay.content}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Available Leads — Pushed by Admin */}
      {availableLeads.length > 0 && (
        <Card className="mb-8 border-2 border-green-200 bg-green-50/20" data-testid="available-leads-section">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="relative">
                <Gift className="w-5 h-5 text-green-600" />
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse" />
              </div>
              <p className="text-sm font-bold text-green-800">
                {availableLeads.length} New Lead{availableLeads.length > 1 ? 's' : ''} Available in Your Area
              </p>
            </div>
            <div className="space-y-3">
              {availableLeads.map(lead => {
                const intentLabel = lead.intent === 'close' ? 'Strike-off' : lead.intent === 'dormant' ? 'Dormant' : lead.intent === 'continue' ? 'Regularise' : '';
                return (
                  <div key={lead.id} className="bg-white rounded-lg border border-green-200 p-4 flex items-center gap-4" data-testid={`available-lead-${lead.id}`}>
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                      <MapPin className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{lead.company_name}</p>
                      <div className="flex items-center gap-3 mt-0.5">
                        {lead.lead_number && <span className="text-[10px] font-bold bg-[#1e3a5f] text-white px-1.5 py-0.5 rounded">{lead.lead_number}</span>}
                        {lead.city && <span className="text-[10px] text-slate-400">{lead.city}</span>}
                        {intentLabel && <span className="text-[10px] text-slate-400">{intentLabel}</span>}
                        <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full font-medium">Website Lead</span>
                      </div>
                    </div>
                    {lead.estimated_savings > 0 && (
                      <div className="text-right shrink-0 mr-2">
                        <p className="text-xs text-slate-400">Est. Savings</p>
                        <p className="text-sm font-bold text-green-700">{'\u20B9'}{Math.round(lead.estimated_savings).toLocaleString('en-IN')}</p>
                      </div>
                    )}
                    <Button
                      onClick={(e) => { e.stopPropagation(); handleClaim(lead.id); }}
                      disabled={claimingId === lead.id}
                      className="bg-green-600 hover:bg-green-700 text-white text-xs px-4 shrink-0"
                      data-testid={`claim-btn-${lead.id}`}
                    >
                      {claimingId === lead.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Claim This Lead'}
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Available Cases — Open Pool (post-conversion, awaiting claim) */}
      {poolCases.length > 0 && (
        <Card className="mb-6 border-2 border-violet-200 bg-violet-50/30" data-testid="pool-cases">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-violet-600" />
                <p className="text-sm font-semibold text-slate-700">
                  {poolCases.length} Case{poolCases.length > 1 ? 's' : ''} Open in Partner Pool
                </p>
              </div>
              <span className="text-[10px] text-violet-600">First to claim wins</span>
            </div>
            <div className="space-y-2">
              {poolCases.slice(0, 8).map(c => (
                <div key={c.id} className="bg-white rounded-lg border border-violet-200 p-3 flex flex-col md:flex-row md:items-center justify-between gap-2" data-testid={`pool-case-${c.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-slate-800 truncate">{c.company_name || 'Untitled Case'}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Created {c.created_at ? new Date(c.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}
                      {c.lead_context?.intent_label ? ` · ${c.lead_context.intent_label}` : ''}
                      {c.lead_context?.filing_summary?.pending_fy_list?.length ? ` · ${c.lead_context.filing_summary.pending_fy_list.length} FYs pending` : ''}
                    </p>
                  </div>
                  <Button size="sm" disabled={claimingCaseId === c.id} onClick={() => handleClaimCase(c.id)} className="bg-violet-600 hover:bg-violet-700 text-white h-7 text-xs gap-1 shrink-0" data-testid={`claim-case-${c.id}`}>
                    {claimingCaseId === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                    Claim
                  </Button>
                </div>
              ))}
              {poolCases.length > 8 && (
                <p className="text-[10px] text-violet-600 text-center pt-1">+ {poolCases.length - 8} more — visit your Cases page to see all.</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Activity Feed */}
      <Card data-testid="activity-feed">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-[#1e3a5f]" />
              <p className="text-sm font-semibold text-slate-700">Recent Activity</p>
            </div>
          </div>

          {activity.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm text-slate-400">No activity yet. Start by creating a new assessment!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activity.map((item, i) => {
                const config = ACTIVITY_ICONS[item.type] || ACTIVITY_ICONS.lead_created;
                const Icon = config.icon;
                const isActionable = item.type === 'report_ready';
                return (
                  <div
                    key={`item-${i}`}
                    className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${isActionable ? 'border-green-200 bg-green-50/30 hover:shadow-sm cursor-pointer' : 'border-slate-100 bg-white'}`}
                    onClick={isActionable ? () => navigate('/r-pace/pipeline') : undefined}
                    data-testid={`activity-item-${i}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${config.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${isActionable ? 'font-semibold text-green-700' : 'text-slate-600'}`}>{item.text}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {item.date ? new Date(item.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                      </p>
                    </div>
                    {isActionable && <ArrowRight className="w-4 h-4 text-green-500 shrink-0 mt-1" />}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
